// Section design tokens (SECTION_V5) ported from sntz_mockups/src/lib/design-tokens.ts.
// Single source of truth for all `/projects` (and future `/assets`, `/chats`, etc.) primitives.
// Mobile-first: 44 px touch floor / 32–36 px desktop. Tailwind v4 compatible.

export const SECTION_V5 = {
    pageShell: "flex min-h-[100dvh] flex-col",
    pageHeader:
        "sticky top-0 z-30 flex h-14 shrink-0 items-center gap-2 border-b bg-background/95 px-4 backdrop-blur supports-[backdrop-filter]:bg-background/80 md:px-8",
    pageHeaderTitle: "text-base font-semibold md:text-lg",
    pageHeaderButton: "min-h-11 md:min-h-9 px-3 gap-1.5",
    pageHeaderIconButton: "size-11 md:size-9",

    bulkBar: "sticky top-14 z-30 flex min-h-[56px] items-center border-b bg-background/95 backdrop-blur",
    bulkBarCounter: "text-sm font-medium tabular-nums",
    bulkBarSpacer: "ml-auto flex items-center gap-2",
    bulkBarButton: "min-h-11 min-w-11 md:h-9 md:min-w-0 px-4 gap-1.5 text-sm",

    toolbar:
        "sticky top-14 z-20 flex flex-col bg-background/95 px-4 py-1.5 backdrop-blur supports-[backdrop-filter]:bg-background/80 md:px-8",
    toolbarRow: "flex items-center gap-2",

    tabsScroller:
        "-mx-4 flex items-center gap-1 overflow-x-auto px-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden snap-x snap-mandatory md:mx-0 md:px-0",
    tabsList: "flex items-center gap-1",
    tab: "snap-start inline-flex min-h-11 min-w-11 md:h-9 md:min-w-0 items-center justify-center gap-1.5 rounded-md px-3 text-sm font-medium text-muted-foreground transition-colors motion-reduce:transition-none hover:bg-accent hover:text-foreground data-[active=true]:bg-accent data-[active=true]:text-foreground",
    tabCount: "tabular-nums text-muted-foreground/70 text-xs",

    search: "h-11 md:h-9",
    searchWrapper: "relative flex-1 min-w-0",
    searchInput:
        "h-11 md:h-9 w-full rounded-md border bg-background pl-9 pr-9 text-sm placeholder:text-muted-foreground focus:outline-none",

    filterPillMobile:
        "inline-flex md:hidden min-h-11 items-center gap-1.5 rounded-md border bg-background px-3 text-sm font-medium",
    filterChipDesktop:
        "hidden md:inline-flex h-9 items-center gap-1.5 rounded-md border bg-background px-3 text-sm font-medium hover:bg-accent transition-colors motion-reduce:transition-none",
    filterIconButton:
        "relative inline-flex h-11 w-11 md:h-9 md:w-9 items-center justify-center rounded-md border bg-background text-muted-foreground hover:bg-accent hover:text-foreground transition-colors motion-reduce:transition-none",
    filterCount:
        "tabular-nums rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-semibold leading-none text-primary-foreground",
    filterCountBadge:
        "absolute -right-1 -top-1 tabular-nums rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-semibold leading-none text-primary-foreground",

    viewSwitcher: "inline-flex h-12 md:h-9 items-center overflow-hidden rounded-md border bg-background",
    viewSwitcherItem:
        "inline-flex h-full w-11 md:w-9 items-center justify-center text-muted-foreground transition-colors motion-reduce:transition-none hover:text-foreground data-[active=true]:bg-accent data-[active=true]:text-foreground",

    counter: "text-xs text-muted-foreground tabular-nums",

    cardsGrid: "grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 px-4 py-4 md:px-8",

    // Dense thumbnail grid (third view mode). Ported from sntz_mockups SECTION_V5.
    // Outer gutter matches the section standard (px-4 py-4 md:px-8) — same as cardsGrid /
    // toolbar / table / pagination — so the grid view aligns with every other view; only the
    // INNER spacing (gap-1) stays denser. See `.claude/rules/section-gutter.md`.
    gridDense:
        "grid grid-cols-3 gap-1 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8 2xl:grid-cols-10 px-4 py-4 md:px-8",
    gridTile:
        "group relative block aspect-square w-full overflow-hidden rounded-md bg-muted ring-1 ring-border/30 transition-[box-shadow] motion-reduce:transition-none hover:ring-border focus-visible:ring-2 focus-visible:ring-ring",
    gridTileMedia: "absolute inset-0 h-full w-full object-cover",
    gridTileOverlay:
        "absolute inset-0 bg-gradient-to-b from-transparent to-black/60 opacity-0 transition-opacity motion-reduce:transition-none group-hover:opacity-100",
    gridTileCornerTL:
        "absolute top-1.5 left-1.5 z-10 inline-flex h-5 items-center gap-1 rounded bg-black/60 px-1.5 text-[10px] font-medium text-white backdrop-blur",
    gridTileTitleBL:
        "absolute bottom-1.5 left-1.5 z-10 inline-flex h-5 max-w-[70%] items-center truncate rounded bg-background/85 px-1.5 text-[10px] font-medium backdrop-blur",
    gridTileBadgeBR:
        "absolute bottom-1.5 right-1.5 z-10 inline-flex h-5 items-center rounded bg-black/60 px-1.5 text-[10px] font-medium text-white",
    // Fixed height so every card in a section is the same size regardless of how many
    // meta fields its footer carries; the media area (cardMedia) flexes to fill the space
    // left by the footer, and the footer keeps all its fields (shrink-0, never clipped).
    card: "group relative flex h-[26rem] flex-col gap-2 overflow-hidden rounded-lg border bg-card text-card-foreground transition-colors motion-reduce:transition-none hover:bg-accent/30",
    /** Poster/media region of a card: fills the height left by the footer (object-cover crops). */
    cardMedia: "relative w-full min-h-0 flex-1 overflow-hidden bg-muted",
    cardContextDots:
        "absolute top-2 right-2 z-10 inline-flex size-11 md:size-9 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground transition-colors motion-reduce:transition-none",
    cardSelectCheckbox:
        "absolute top-2 left-2 z-10 inline-flex size-11 md:size-9 items-center justify-center rounded-md",

    tableWrapper: "w-full",
    tableRoot: "w-full caption-bottom text-sm",
    tableHead: "sticky top-0 z-10 bg-background/95 backdrop-blur border-b border-border/40",
    tableHeadCell:
        "h-10 px-3 text-left align-middle text-xs font-medium text-muted-foreground first:pl-4 last:pr-4 md:first:pl-8 md:last:pr-8",
    tableRow: "border-b transition-colors motion-reduce:transition-none hover:bg-accent/30",
    tableCell: "px-3 py-3 align-top first:pl-4 last:pr-4 md:first:pl-8 md:last:pr-8",
    tableCellHidden: {
        md: "hidden md:table-cell",
        lg: "hidden lg:table-cell",
        xl: "hidden xl:table-cell",
    },

    rowContextTrigger:
        "inline-flex size-11 md:size-9 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground transition-colors motion-reduce:transition-none",
    rowContextItem: "min-h-11 md:min-h-9 gap-2 text-sm",

    emptyState: "flex flex-col items-center justify-center gap-3 py-16 text-center",
    emptyStateTitle: "text-base font-medium",
    emptyStateBody: "max-w-sm text-sm text-muted-foreground",

    toolbarActions: "flex items-center gap-2 shrink-0 pl-2",

    scrollContainer: "flex-1 overflow-y-auto min-h-0 [overscroll-behavior-y:contain]",

    touchTargetMobilePx: 44,
    touchTargetDesktopPx: 32,
} as const;
